import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { PortalNavbarComponent } from './portal-navbar.component';
import { HelcimCustomerListResponse, TestPaymentService } from '../services/test-payment.service';

interface QuickLink {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-payment-services-customer-get-customers-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Customer > Get Customers</h1>
          <p class="page-sub">Search and page through Helcim customers. Organization selection is optional and acts as a filter context.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="related-card related-card--top">
            <h3>Quick Access</h3>
            <div class="related-grid">
              @for (link of relatedLinks; track link.route) {
                <a class="quick-card" [routerLink]="link.route">
                  <div class="quick-card-hd">
                    <span class="quick-icon">{{ link.icon }}</span>
                    <span>{{ link.label }}</span>
                  </div>
                </a>
              }
            </div>
          </div>

          <div class="related-card related-card--top">
            <h3>Organization Filter Context (Optional)</h3>
            <label class="field">
              <span>Organization</span>
              <select [(ngModel)]="organizationId" name="organizationId" (ngModelChange)="onOrganizationChange()" [disabled]="isLoadingOrganizations || isLoading">
                <option [ngValue]="0">{{ isLoadingOrganizations ? 'Loading organizations...' : 'All organizations / no filter' }}</option>
                @for (organization of organizations; track organization.id) {
                  <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                }
              </select>
            </label>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Get Customers</span>
            </div>

            <div class="form">
              <label class="field"><span>Search (string)</span><input type="text" [(ngModel)]="search" name="search" maxlength="120" /></label>
              <label class="field"><span>Customer Code (string)</span><input type="text" [(ngModel)]="customerCode" name="customerCode" maxlength="64" /></label>
              <label class="field"><span>Limit (number)</span><input type="number" [(ngModel)]="limit" name="limit" min="1" max="250" /></label>
              <label class="field"><span>Page (number)</span><input type="number" [(ngModel)]="page" name="page" min="1" /></label>
              <label class="field field--checkbox">
                <span>Include Cards (boolean)</span>
                <input type="checkbox" [(ngModel)]="includeCards" name="includeCards" />
              </label>
              <div class="actions">
                <button type="button" class="btn btn--primary" [disabled]="isLoading" (click)="getCustomers()">
                  {{ isLoading ? 'Loading...' : 'Get Customers' }}
                </button>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            <div class="grid-wrap">
              <div class="grid-title">Response Grid</div>
              <table class="result-grid">
                <thead>
                  <tr>
                    <th>Customer Id</th>
                    <th>Customer Code</th>
                    <th>Contact Name</th>
                    <th>Business Name</th>
                    <th>Cell Phone</th>
                  </tr>
                </thead>
                <tbody>
                  @for (customer of customers; track customer.customerId) {
                    <tr (dblclick)="openCustomer(customer)">
                      <td>{{ customer.customerId }}</td>
                      <td>{{ customer.customerCode || 'N/A' }}</td>
                      <td>{{ customer.contactName || 'N/A' }}</td>
                      <td>{{ customer.businessName || 'N/A' }}</td>
                      <td>{{ customer.cellPhone || 'N/A' }}</td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="5" class="empty-row">No customers loaded yet.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="paging">
              <div class="paging-info">
                <span><strong>Page:</strong> {{ resolvedPage }}</span>
                <span><strong>Limit:</strong> {{ resolvedLimit }}</span>
                <span><strong>Total:</strong> {{ response?.totalCount ?? 0 }}</span>
              </div>
              <div class="paging-actions">
                <button type="button" class="btn btn--ghost" [disabled]="isLoading || resolvedPage <= 1" (click)="previousPage()">Previous</button>
                <button type="button" class="btn btn--ghost" [disabled]="isLoading || !hasNextPage" (click)="nextPage()">Next</button>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card{padding-bottom:1rem}.related-card{padding:1rem 1.1rem}.related-card--top{margin-bottom:1rem}.related-card h3{margin:0 0 .55rem}.related-grid{display:grid;gap:.8rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:.85rem 1rem;text-decoration:none;color:var(--color-text);transition:transform .15s ease,border-color .15s ease}.quick-card:hover{transform:translateY(-2px);border-color:color-mix(in srgb,var(--color-accent) 38%,var(--color-border))}.quick-card-hd{display:flex;align-items:center;gap:.5rem}.quick-icon{display:inline-flex;align-items:center;justify-content:center;width:1.2rem;height:1.2rem}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--checkbox{align-items:flex-start;justify-content:flex-end}.field input[type='text'],.field input[type='number'],.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.field input[type='checkbox']{margin-top:.2rem}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.btn--ghost{background:transparent;border-color:var(--color-border);color:var(--color-text)}.grid-wrap{padding:0 1rem}.grid-title{font-weight:600;margin-bottom:.5rem}.result-grid{border-collapse:collapse;width:100%}.result-grid th,.result-grid td{border:1px solid var(--color-border);padding:.5rem .55rem;text-align:left;vertical-align:top}.result-grid th{background:color-mix(in srgb,var(--color-surface-1) 75%,transparent)}.result-grid tbody tr:hover{background:color-mix(in srgb,var(--color-accent) 8%,transparent);cursor:pointer}.empty-row{color:var(--color-muted);text-align:center}.paging{align-items:center;display:flex;flex-wrap:wrap;gap:.8rem;justify-content:space-between;padding:1rem 1rem 0}.paging-info{display:flex;flex-wrap:wrap;gap:.7rem}.paging-actions{display:flex;gap:.6rem}.result{margin:0 1rem .9rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesCustomerGetCustomersPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  organizationId = 0;
  search = '';
  customerCode = '';
  limit = 25;
  page = 1;
  includeCards = false;
  organizations: OrganizationDto[] = [];
  isLoadingOrganizations = false;

  isLoading = false;
  errorMessage = '';
  response: HelcimCustomerListResponse | null = null;

  readonly relatedLinks: QuickLink[] = [
    { label: 'Get Customer', route: '/payment-services/customer/get-customer', icon: '👤' }
  ];

  async ngOnInit(): Promise<void> {
    await this.loadOrganizationsAsync();
  }

  onOrganizationChange(): void {
    this.customerCode = this.organizationId > 0 ? String(this.organizationId) : '';
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }

      this.onOrganizationChange();
    } catch {
      this.organizations = [];
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  openCustomer(customer: { customerId: string; organizationId: number }): void {
    const customerId = customer.customerId?.trim();
    if (!customerId) {
      return;
    }

    const targetOrganizationId = customer.organizationId > 0
      ? customer.organizationId
      : this.organizationId;

    void this.router.navigate(['/payment-services/customer/get-customer'], {
      queryParams: {
        organizationId: targetOrganizationId > 0 ? targetOrganizationId : undefined,
        customerId
      }
    });
  }

  get customers() {
    return this.response?.customers ?? [];
  }

  get resolvedPage(): number {
    return this.response?.page ?? this.page;
  }

  get resolvedLimit(): number {
    return this.response?.limit ?? this.limit;
  }

  get hasNextPage(): boolean {
    if (!this.response) {
      return false;
    }

    const totalCount = this.response.totalCount ?? 0;
    const page = this.response.page ?? this.page;
    const limit = this.response.limit ?? this.limit;
    return page * limit < totalCount;
  }

  async getCustomers(targetPage?: number): Promise<void> {
    this.errorMessage = '';

    if (this.limit <= 0) {
      this.errorMessage = 'Limit must be greater than zero.';
      return;
    }

    const page = targetPage && targetPage > 0 ? targetPage : this.page;

    this.isLoading = true;

    try {
      const result = await this.testPaymentService.getCustomers({
        organizationId: this.organizationId > 0 ? this.organizationId : undefined,
        search: this.search.trim() || undefined,
        customerCode: this.customerCode.trim() || undefined,
        limit: this.limit,
        page,
        includeCards: this.includeCards
      }).toPromise();

      if (!result) {
        throw new Error('Get Customers request returned no data.');
      }

      this.response = result;
      this.page = result.page ?? page;
      this.limit = result.limit ?? this.limit;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Get Customers request failed.';
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  previousPage(): Promise<void> {
    return this.getCustomers(this.resolvedPage - 1);
  }

  nextPage(): Promise<void> {
    return this.getCustomers(this.resolvedPage + 1);
  }
}
